/**
 * `LinearRewardsErc4626` APR maths — shared by every vault of that family.
 *
 * The family (Frax's `sfrxUSD` / `sFRAX`, Resupply's `sreUSD`, …) streams a
 * fixed reward amount linearly over a cycle instead of rebasing or compounding
 * a utilisation curve. Three reads describe the whole rate:
 *
 *   rewardsCycleData()                → (cycleEnd, lastSync, rewardCycleAmount)
 *   maxDistributionPerSecondPerAsset()→ the governance cap
 *   storedTotalAssets()               → the denominator
 *
 * and the rate is
 *
 *   perSecond = min(rewardCycleAmount / (cycleEnd − lastSync),
 *                   cap × storedTotalAssets / 1e18)
 *   APR       = perSecond × secondsPerYear / storedTotalAssets
 *
 * **The cap is not a formality — in practice it IS the rate** whenever the
 * distributor over-funds a cycle, which is why it is applied before
 * annualising rather than treated as an upper bound to check afterwards.
 *
 * Annualised LINEARLY (nominal APR), the package-wide convention: these vaults
 * do not compound within a cycle, and reporting an APY here would disagree
 * with every other savings row.
 */

const YEAR_SECONDS = 31_536_000n
const ONE_E18 = 10n ** 18n

/** The three reads a `LinearRewardsErc4626` needs, in call order. */
export const LINEAR_REWARDS_APR_ABI = [
  {
    name: 'rewardsCycleData',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'cycleEnd', type: 'uint40' },
      { name: 'lastSync', type: 'uint40' },
      { name: 'rewardCycleAmount', type: 'uint216' },
    ],
  },
  {
    name: 'maxDistributionPerSecondPerAsset',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'storedTotalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** Call list for one vault, in the order {@link linearRewardsApr} slices. */
export const linearRewardsCalls = (address: string) => [
  { address, name: 'rewardsCycleData', params: [] },
  { address, name: 'maxDistributionPerSecondPerAsset', params: [] },
  { address, name: 'storedTotalAssets', params: [] },
]

/** Number of reads {@link linearRewardsCalls} emits per vault. */
export const LINEAR_REWARDS_READS = 3

/**
 * APR in PERCENT from a `[rewardsCycleData, cap, storedTotalAssets]` slice of
 * multicall results starting at `offset`. Returns 0 on an empty vault or an
 * unreadable slice rather than throwing — a savings row with no rate is far
 * better than a fetch that takes the whole batch down.
 */
export function linearRewardsApr(results: unknown[], offset = 0): number {
  const cycle = results[offset] as
    | [number | bigint, number | bigint, bigint]
    | undefined
  const maxPerSecondPerAsset = results[offset + 1]
  const storedTotalAssets = results[offset + 2]
  if (
    !Array.isArray(cycle) ||
    typeof maxPerSecondPerAsset !== 'bigint' ||
    typeof storedTotalAssets !== 'bigint' ||
    storedTotalAssets === 0n
  ) {
    return 0
  }

  const [cycleEnd, lastSync, rewardCycleAmount] = cycle
  const window = BigInt(cycleEnd) - BigInt(lastSync)
  const uncapped = window > 0n ? rewardCycleAmount / window : 0n
  const cap = (maxPerSecondPerAsset * storedTotalAssets) / ONE_E18
  const perSecond = uncapped < cap ? uncapped : cap
  return Number((perSecond * YEAR_SECONDS * ONE_E18) / storedTotalAssets) / 1e16
}
